import { fromJS, is, Map, merge } from 'immutable';
import { findPropertiesByType, getSchemaByType, findPropertiesByScope } from '@pearsontechnology/aero-plugin-schema';
import { isImmutable } from 'pluginServices/helpers/immutableHelpers';
import { LEARNER, EDITOR } from 'pluginServices/constants/context';

import AssetManager from 'AssetManager';
import ActivityResolver from 'resolvers/RTMResolvers/ActivityResolver';
import interactiveService from 'services/InteractiveService';
import componentService from 'services/ComponentService';
import scenarioService from 'services/ScenarioService';
import previewService from 'services/PreviewService';
import pathwayService from 'services/PathwayService';
import alfrescoService from 'services/AlfrescoService';
import annotationService from 'services/AnnotationService';
import CoursewareTargetService from 'pluginServices/services/CoursewareTargetService';
import * as pathwayTypes from 'services/PathwayService/constants';
import scopeBindingManager from 'ScopeBindingManager';

import { createVersionExpression } from 'utils/versionExpressions';
import { composeState } from 'utils/scope/composeScope';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import messages from 'containers/App/messages';

import pluginTypes from 'constants/pluginTypes';

import {
  safeParseJSON,
  addChildInConfig,
  createDefaultConfig,
  getInActivityConfigHelper,
  getInThemeHelper,
  getInThemeVariantConfigHelper,
} from './utils';
import {
  CONFIG_CHANGE,
  THEME_CHANGE,
  CREATED,
  DELETED,
  DUPLICATED,
  ALFRESCO_ASSETS_UPDATE,
} from './activityChangeTypes';
import * as elementTypes from './elementTypes';
import * as keyPaths from './keyPaths';
import PluginService from '../PluginService';
import WorkspaceService from '../WorkspaceService';

/**
 * A published activity. I.e., an activity that may be loaded in the learnspace.
 * @typedef {Object} DeploymentDTO
 * @property {string=} deploymentId Unspecified if the deployment has yet to be created.
 * @property {string} activityId The deployed activity.
 * @property {string} cohortId The cohort which owns the deployment.
 */

/**
 * Builds a service to access activities, and hold all of their business
 * logic.  Activities are courseware items that may hold student progress and
 * pathways, effectively making them containers.
 * See [Activity in the aero docs](https://dev-developers.smartsparrow.com/platform/courseware-items/#activity)
 */
export default (() => {
  /**
   * @type {ActivityResolver}
   */
  let activityResolver;
  let isRegistered = false;

  let _activityCache = Map();
  const _subscriptions = {};
  const _rtmUnsubscribeFunctions = {};
  let _registerSubscriptionsInProgress = [];

  /**
   * Asynchronously wait for a workspaceId to be in the local storage.
   * This was added in last minute to remove the hardcoded workspaceId
   * which would have crashed the move to production.
   * TODO: make this less of a hack/write tests.
   */
  const getWorkspaceId = () => new Promise((resolve) => {
    (function waitForWorkspaceId() {
      const workspaceId = WorkspaceService.getSelectedWorkspaceId();
      if (workspaceId) {
        resolve(workspaceId);
      } else {
        setTimeout(waitForWorkspaceId, 50);
      }
    }());
  });

  function _getActivityConfig(activityId) {
    return _activityCache.getIn([activityId, keyPaths.CONFIG_KEY]);
  }

  function _getActivityTheme(activityId) {
    return _activityCache.getIn([activityId, keyPaths.THEME_KEY]);
  }

  function _getSelectedTheme(activityId) {
    const variants = _activityCache.getIn([activityId, keyPaths.THEME_VARIANT_KEYS]);
    const defaultVariant = variants && variants.find((variant) => variant.state === 'DEFAULT');
    return defaultVariant?.config;
  }

  function _forgetActivity(activityId) {
    _activityCache = _activityCache.remove(activityId);

    if (_rtmUnsubscribeFunctions[activityId]) {
      // remove subscription from requestTable of RTM and
      // then the RTM calls author.activity.unsubscribe
      _rtmUnsubscribeFunctions[activityId]();
      _rtmUnsubscribeFunctions[activityId] = null;
    }
  }

  function updateAssetManager(assets) {
    if (assets) {
      AssetManager.addResolvedToCache(assets);
    }
  }

  function _createUnsubscribe(activityId, listener) {
    return () => {
      const listenerIndex = _subscriptions[activityId].indexOf(listener);
      _subscriptions[activityId].splice(listenerIndex, 1);
      if (_subscriptions[activityId].length === 0) {
        _forgetActivity(activityId);
      }
    };
  }

  function _notifyListeners(activityId, context = EDITOR) {
    if (_subscriptions[activityId]) {
      const listeners = _subscriptions[activityId];
      const updatedActivityObject = (context === EDITOR ? _activityCache.get(activityId).toJS() : previewService.getInPreviewScope([activityId]));
      listeners.forEach((listener) => {
        listener(updatedActivityObject);
      });
    }
  }

  function notifyUpdate(activityId) {
    _notifyListeners(activityId);
  }

  const findDefaultVariant = (variant) => variant.state === 'DEFAULT';

  function _parseAndSaveToCache(activity) {
    const jsConfig = safeParseJSON(activity[keyPaths.CONFIG_KEY]);
    const oldConfig = _getActivityConfig(activity.activityId);

    const jsActivityTheme = safeParseJSON(activity[keyPaths.THEME_KEY]);
    const oldActivityTheme = _getActivityTheme(activity.activityId);

    const defaultVariant = activity.themePayload?.themeVariants?.find(
      findDefaultVariant
    );
    const jsVariantConfig = defaultVariant && safeParseJSON(defaultVariant.config);
    const oldSelectedTheme = _getSelectedTheme(activity.activityId);

    const jsActivity = {
      ...activity,
      [keyPaths.CONFIG_KEY]: jsConfig,
      [keyPaths.THEME_KEY]: jsActivityTheme,
    };

    if (jsVariantConfig) {
      const defaultConfig = jsActivity.themePayload?.themeVariants?.find(
        findDefaultVariant
      );
      if (defaultConfig) {
        defaultConfig.config = jsVariantConfig;
      }
    }

    updateAssetManager(activity.assets);
    const immutableActivity = fromJS(jsActivity);
    _activityCache = _activityCache.set(activity.activityId, immutableActivity);

    // notify only if the activity.config or themes are different to what we already have
    if (!is(oldConfig, fromJS(jsConfig)) ||
      !is(oldActivityTheme, fromJS(jsActivityTheme)) ||
      (!is(oldSelectedTheme, fromJS(jsVariantConfig)))
    ) {
      _notifyListeners(activity.activityId);
    }
  }

  async function setComponentInActivity(activityId, componentId) {
    const COMPONENTS_PATH = 'components';
    const existingComponents = await getInActivity(
      activityId,
      [COMPONENTS_PATH]
    ) || [];
    existingComponents.push(componentId);
    _activityCache = _activityCache.setIn([activityId, COMPONENTS_PATH], fromJS([...existingComponents, componentId]));
  }

  function setAncestorIdsInActivity(activityId, projectId, workspaceId) {
    if (has(activityId)) {
      _activityCache = _activityCache.setIn([activityId, keyPaths.ANCESTORS_KEY], fromJS({ projectId, workspaceId }));
    }
  }

  /**
 * Fetches the Plugin Schema, given a pluginId and version
 * @todo Remove this.  Client code should use the PluginService directly
 * @param {string} pluginId
 * @param {string} pluginVersion
 */
  const getPluginSchema = async (pluginId, pluginVersion) => {
    const response = await PluginService.getPlugin(pluginId, pluginVersion);
    return JSON.parse(response.manifest.configurationSchema);
  };

  /**
   * Sets the new config in Preview scope and notify listeners
   * @param {string} activityId
   * @param {*} newScope
   */
  const setInPreviewScope = async (activityId, newScope) => {
    const config = _activityCache.getIn([activityId, keyPaths.CONFIG_KEY]);
    const { pluginId, version } = _activityCache.getIn([activityId, 'plugin']).toJS();
    const schema = await getPluginSchema(pluginId, version);
    const previewState = composeState(schema, config, newScope);
    previewService.setPreviewScope([activityId], previewState);
    // save and notify only if the component config is different to what we already have
    if (!is(config, fromJS(previewState))) {
      _notifyListeners(activityId, LEARNER);
    }
  };

  // This is how we can get an invalid cache (not waiting for backend response)
  function _dangerouslySetConfigInCache(activityId, activityConfig) {
    const jsConfig = isImmutable(activityConfig) ? activityConfig.toJS() : activityConfig;
    const immutableConfig = fromJS(jsConfig);

    // save and notify only if the activityConfig is different to what we already have
    const oldConfig = _activityCache.getIn([activityId, keyPaths.CONFIG_KEY]);
    if (!is(oldConfig, immutableConfig)) {
      _activityCache = _activityCache.setIn([activityId, keyPaths.CONFIG_KEY], immutableConfig);
      _notifyListeners(activityId);
    }
  }

  function _dangerouslySetThemeInCache(activityId, themeConfig) {
    const jsConfig = isImmutable(themeConfig) ? themeConfig.toJS() : themeConfig;
    const immutableConfig = fromJS(jsConfig);

    // save and notify only if the activityConfig is different to what we already have
    const oldConfig = _activityCache.getIn([activityId, keyPaths.THEME_KEY]);
    if (!is(oldConfig, immutableConfig)) {
      _activityCache = _activityCache.setIn([activityId, keyPaths.THEME_KEY], immutableConfig);
      _notifyListeners(activityId);
    }
  }

  function _receiveActivityChange(broadcast) {
    const change = broadcast.response;
    switch (change.elementType) {
      case elementTypes.ACTIVITY: {
        const activityExists = _activityCache.has(change.elementId);
        const activity = activityExists && _activityCache.get(change.elementId).toJS();

        if (change.action === CONFIG_CHANGE && activityExists) {
          activity.config = change.config;
          _parseAndSaveToCache(activity);
        } else if (change.action === THEME_CHANGE && activityExists) {
          activity[keyPaths.THEME_KEY] = change.theme;
          _parseAndSaveToCache(activity);
        } else if (change.action === CREATED || change.action === DELETED || change.action === DUPLICATED) {
          // notify the parent pathway
          pathwayService.notifyUpdate(change.parentPathwayId);
        } else if (change.action === ALFRESCO_ASSETS_UPDATE) {
          alfrescoService.notifyUpdateActivityChange(broadcast);
        }
        break;
      }

      case elementTypes.INTERACTIVE: {
        interactiveService.receiveInteractiveChange(broadcast);
        break;
      }

      case elementTypes.COMPONENT: {
        componentService.receiveComponentChange(broadcast);
        break;
      }

      case elementTypes.SCENARIO: {
        scenarioService.receiveScenarioChange(broadcast);
        break;
      }

      case elementTypes.PATHWAY: {
        pathwayService.receivePathwayChange(broadcast);
        break;
      }

      default: {
        break;
      }
    }
  }

  function _clearSubscriptionInProgress(activityId) {
    _registerSubscriptionsInProgress = _registerSubscriptionsInProgress.filter((id) => id !== activityId);
  }

  async function _subscribeActivityToNetworkChanges(activityId) {
    if (!_rtmUnsubscribeFunctions[activityId] && !_registerSubscriptionsInProgress.includes(activityId)) {
      _registerSubscriptionsInProgress.push(activityId);
      _rtmUnsubscribeFunctions[activityId] =
        await activityResolver.authorActivitySubscribe({ activityId }, _receiveActivityChange);
      _clearSubscriptionInProgress(activityId);
    }
  }

  /**
   * Builds a new activity.
   * @param {string} pluginId
   * @param {string} fullPluginVersion
   * @param {*} schema
   * @param {*} activityConfig
   * @param {boolean} isWorkspaceActivity
   * @param {string} parentPathwayId
   */
  const createActivity = async (pluginId, fullPluginVersion, schema, activityConfig, isWorkspaceActivity, parentPathwayId, isProjectActivity = false, projectId) => {
    const defaultConfig = createDefaultConfig(schema);
    // TODO: implement `Untitled` in a better way
    const title = activityConfig.title || defaultConfig.title || 'Untitled';

    const config = { ...defaultConfig, ...activityConfig, title, __metadata: { pluginVersion: fullPluginVersion } };

    const description = activityConfig.description || defaultConfig.description;
    if (description) {
      config.description = description;
    }
    const pluginVersion = createVersionExpression(fullPluginVersion);

    let createMethod = activityResolver.authorActivityCreate;
    const payload = { pluginId, pluginVersion, [keyPaths.CONFIG_KEY]: JSON.stringify(config), parentPathwayId };

    if (isWorkspaceActivity || isProjectActivity) {
      const themeSchema = getSchemaByType('theme');
      const themeConfig = createDefaultConfig(themeSchema);
      payload.theme = JSON.stringify(themeConfig);
    }
    if (isProjectActivity) {
      createMethod = activityResolver.projectActivityCreate;
      payload.projectId = projectId;
    }

    const newActivity = await createMethod(payload);
    const newActivityWithPluginVersion = { ...newActivity, plugin: { ...newActivity.plugin, latestVersion: fullPluginVersion } };
    _parseAndSaveToCache(newActivityWithPluginVersion);
    if (parentPathwayId) {
      pathwayService.notifyUpdate(parentPathwayId);
    }

    const learnerEditableProperties = findPropertiesByScope(schema);
    if (learnerEditableProperties.length) {
      await authorStudentScopeRegisterActivity(newActivity.activityId);
    }

    return newActivity.activityId;
  };

  const createWorkspaceActivity = (pluginId, pluginVersion, schema, activityConfig) =>
    createActivity(pluginId, pluginVersion, schema, activityConfig, true);

  const createProjectActivity = (pluginId, pluginVersion, schema, activityConfig, projectId) =>
    createActivity(pluginId, pluginVersion, schema, activityConfig, false, undefined, true, projectId);

  const createInActivity = async (pluginId, pluginVersion, schema, activityConfig, parentInfo, isWorkspaceActivity) => {
    const newActivityId = await createActivity(pluginId, pluginVersion, schema, activityConfig, isWorkspaceActivity);
    const newActivityConfig = _getActivityConfig(newActivityId);
    const newChildItem = {
      itemId: newActivityId,
      itemName: newActivityConfig.get('title'),
    };
    return addChildInActivity(parentInfo, newChildItem);
  };

  const addChildInActivity = async (parentInfo, newChildItem) => {
    const { parentActivityId, parentPluginSchema, parentActivityPath } = parentInfo;
    const parentConfig = _getActivityConfig(parentActivityId);
    const newParentConfig = addChildInConfig(
      newChildItem, parentPluginSchema, parentActivityPath, parentConfig
    );
    await saveActivity(parentActivityId, newParentConfig);
    return newChildItem.itemId;
  };

  /**
   * Saves a complete config object for an activity
   * @param {string} activityId
   * @param {object} activityConfig
   */
  const saveActivity = async (activityId, activityConfig) => {
    let currentConfig = activityConfig;
    _dangerouslySetConfigInCache(activityId, activityConfig);
    if (currentConfig instanceof Map) {
      currentConfig = currentConfig.toJS();
    }

    const activityConfigWithMetadata = { ...currentConfig, __metadata: { pluginVersion: _activityCache.get(activityId).toJS()?.plugin?.latestVersion } };

    const payload = { activityId, config: JSON.stringify(activityConfigWithMetadata) };
    await activityResolver.authorActivityConfigReplace(payload);
    scopeBindingManager.saveBoundReferences(activityId, currentConfig);

    // TODO: Diff the response from server maybe using delta changes or
    // having an ordered list of requests. This is to update our cache in case
    // the backend changed in a different way than how we applied the change.
    // const updatedActivity = await activityResolver.authorActivityConfigReplace(payload);
    // _parseAndSaveToCache(updatedActivity);

    return activityId;
  };

  /**
   * Saves a complete theme config object for an activity
   * @param {string} activityId
   * @param {object} themeConfig
   */
  const saveActivityTheme = async (activityId, themeConfig) => {
    _dangerouslySetThemeInCache(activityId, themeConfig);
    await activityResolver.authorActivityThemeReplace({
      activityId,
      config: JSON.stringify(themeConfig),
    });
  };

  const authorActivityIconLibraryAssociate = async ({
    activityId,
    iconLibraries,
  }) => {
    const response = await activityResolver.authorActivityIconLibraryAssociate({
      activityId,
      iconLibraries,
    });
    return response;
  };

  const saveActivityThemePayload = async (activityId, themePayload) => {
    const jsThemePayload = isImmutable(themePayload) ? themePayload.toJS() : themePayload;
    const defaultVariant = themePayload.themeVariants?.find(
      findDefaultVariant
    );
    if (defaultVariant) {
      const jsVariantConfig = safeParseJSON(defaultVariant.config);
      if (jsVariantConfig) {
        defaultVariant.config = jsVariantConfig;
      }
    }
    const immutableThemePayload = fromJS(jsThemePayload);

    // save and notify only if the thePayload is different to what we already have
    const oldThemePayload = _activityCache.getIn([activityId, keyPaths.THEME_PAYLOAD_KEY]);
    if (!is(oldThemePayload, immutableThemePayload)) {
      _activityCache = _activityCache.setIn([activityId, keyPaths.THEME_PAYLOAD_KEY], immutableThemePayload);
      _notifyListeners(activityId);
    }
  };

  const saveInActivityConfig = (activityId, keyPath, newConfigPart = null) => {
    const oldConfig = _getActivityConfig(activityId);
    const newConfig = keyPath.length ? oldConfig.setIn(keyPath, newConfigPart) : merge(oldConfig, newConfigPart);
    return saveActivity(activityId, newConfig);
  };

  const saveInActivityIngestionUpdate = (activityId, keyPath, newConfigPart = null) => {
    _activityCache = _activityCache.setIn([activityId, keyPath], newConfigPart);
    _notifyListeners(activityId);
  };

  const saveInActivityPreviewScope = (activityId, keyPath, newConfigPart) => {
    const oldConfig = _getActivityConfig(activityId);
    const newScope = oldConfig.setIn(keyPath, newConfigPart);
    setInPreviewScope(activityId, newScope);
    return activityId;
  };

  const has = (id) => (
    _activityCache.has(id)
  );

  /**
   * Returns an activity, possibly fetching it if it isn't already in
   * cache.
   * @param {Activity} activityId
   * @param {boolean} bypassCache - should be used in exceptional cases only to get
   * fresh activity data. Typical case is for tagged objectives which are updated by graphql calls
   * but on the same activity cache is not updated with the linked doc info
   * @param {boolean} subscribeActivityToNetworkChanges
   */
  const getActivity = async (activityId, bypassCache = false, subscribeActivityToNetworkChanges = true) => {
    if (!activityId) {
      return null;
    }
    if (!has(activityId) || bypassCache) {
      const activity = await activityResolver.authorActivityGet({ activityId });
      if (!activity) return null; // unhappy path
      _parseAndSaveToCache(activity);
      if (!(activity.parentPathwayId) && subscribeActivityToNetworkChanges) {
        _subscribeActivityToNetworkChanges(activityId);
      }
    }
    const activity = _activityCache.get(activityId).toJS();
    scopeBindingManager.saveBoundReferences(activityId, activity.config);

    return activity;
  };

  /**
   * Returns activity's data at a particular path.
   * @param {string} activityId
   * @param {[string]} keyPath
   */
  const getInActivity = async (activityId, keyPath = []) => {
    await getActivity(activityId);
    const selectedData = _activityCache.getIn([activityId, ...keyPath]);

    if (selectedData === null || typeof selectedData === 'undefined') {
      return selectedData;
    }
    return isImmutable(selectedData) ? selectedData.toJS() : selectedData;
  };

  /**
   * Get a subsection of an Activity's configuration.
   * @param {string} activityId
   * @param {[string]} keyPath subsection of the activity.
   */
  const getInActivityConfig = async (activityId, keyPath = []) => (
    getInActivity(activityId, [keyPaths.CONFIG_KEY, ...keyPath])
  );

  /**
   * Get a subsection of an Activity's theme.
   * @param {string} activityId
   * @param {[string]} keyPath subsection of the activity.
   */
  const getInActivityTheme = async (activityId, keyPath = []) => (
    getInActivity(activityId, [keyPaths.THEME_KEY, ...keyPath])
  );

  /**
   * Returns an activities plugin data
   * @param {Activity} activityId
   */
  const getActivityPlugIn = async (activityId) => {
    if (!activityId) {
      return null;
    }
    const activity = await activityResolver.authorActivityGetPlugin({ activityId });
    if (!activity) return null; // unhappy path
    return activity;
  };

  /**
  * Get the selected variant theme of an Activity.
  * @param {string} activityId
  */
  const getInSelectedTheme = async (activityId) => {
    const variants = await getInActivity(activityId, keyPaths.THEME_VARIANT_KEYS);
    const defaultVariant = variants && variants.find((variant) => variant.state === 'DEFAULT');
    return defaultVariant?.config;
  };

  /**
   * Get a list of all the Activities belonging to the active workspace
   */
  const getWorkspaceActivityList = async () => {
    const workspaceId = await getWorkspaceId();
    const activities = await activityResolver.workspaceActivityList({ workspaceId });
    return activities.map((activity) => ({ ...activity, config: safeParseJSON(activity.config) || {} }));
  };

  /**
   * Create or update a deployed activity.
   * @param {DeploymentDTO} deployment the {@link DeploymentDTO} in which to publish.  If the
   *   deploymentId is specified, this function will update an existing deployment.
   * @returns {DeploymentDTO} published deployment
   */
  const publishWorkspaceActivity = async ({ cohortId, activityId, deploymentId }) => {
    const workspaceId = await getWorkspaceId();
    const response = await activityResolver.workspaceActivityPublish({ workspaceId, activityId, cohortId, deploymentId });
    return response;
  };

  /**
   * Creates a subscription to an activity.
   * @param {string} activityId
   * @param {function(*)} listener A callback invoke on subscription, and every change to the
   * activity afterwards
   */
  const subscribeToActivity = (activityId, listener) => {
    _subscriptions[activityId] = _subscriptions[activityId] || [];
    _subscriptions[activityId].push(listener);

    if (has(activityId)) {
      _subscribeActivityToNetworkChanges(activityId); // calls author.activity.subscribe when a course is created.
      listener(_activityCache.get(activityId).toJS());
    } else {
      getActivity(activityId);
    }

    return _createUnsubscribe(activityId, listener);
  };

  const subscribeToActivityConfigPart = (activityId, keyPath = [], configPartListener) => {
    // TODO: subscribe to only a part of the activity (It would mean knowing when only
    // that part changed instead of knowing that the whole activity changed).
    const listener = (updatedActivity) => {
      const updatedValue = getInActivityConfigHelper(updatedActivity, keyPath);
      configPartListener(updatedValue);
    };

    return subscribeToActivity(activityId, listener);
  };

  const subscribeToThemePart = async (activityId, keyPath = [], themePartListener) => {
    // TODO: subscribe to only a part of the theme (It would mean knowing when only
    // that part changed instead of knowing that the whole activity changed).
    const listener = (updatedActivity) => {
      const updatedValue =
        (getInThemeVariantConfigHelper(updatedActivity, keyPath))
        || getInThemeHelper(updatedActivity, keyPath);
      themePartListener(updatedValue);
    };

    return subscribeToActivity(activityId, listener);
  };

  /**
   * Allows deletion of child activities.  Does not facilitate the deletion of root activities.
   * @param {string} activityId
   * @param {string} parentPathwayId
   */
  const deleteActivity = async (activityId, parentPathwayId) => {
    await authorStudentScopeDeregisterActivity(activityId);
    await activityResolver.authorActivityDelete({
      activityId,
      parentPathwayId,
    });
    pathwayService.notifyUpdate(parentPathwayId);
  };

  /**
   * Moves activities between activities
   * @param {string} activityId Id of the moving item
   * @param {string} newPathwayId Id of the destination pathway
   * @param {string} parentPathwayId Id of the shared parent of pathwayId and oldPathwayId
   * @param {number} payload.index Index in the new pathway
   * @param {string} oldPathwayId Id of the source pathway
   */
  const moveActivity = async (activityId, newPathwayId, parentPathwayId, index, oldPathwayId, isIndexAllowed = false) => {
    const activity = await activityResolver.authorActivityMove({
      activityId,
      pathwayId: newPathwayId,
      index,
      isIndexAllowed,
    });
    _parseAndSaveToCache(activity);
    const target = await CoursewareTargetService.build().createTarget(activityId);
    await annotationService.refetchAnnotations(target);
    await pathwayService.notifyUpdate(parentPathwayId);
    await pathwayService.notifyUpdate(newPathwayId);
    if (oldPathwayId) {
      await pathwayService.notifyUpdate(oldPathwayId);
    }
  };

  /**
   * Duplicates a child activity. That is an activity belonging to a pathway.
   * @todo It seems like the activityConfig isn't actually used for anything, and
   * can be removed from parameter list.
   * @param {string} activityId
   * @param {string} parentPathwayId The pathway which owns the target activity
   * @param {*} activityConfig the activity
   * @returns {string} new activityId
   */
  const duplicateActivity = async (activityId, parentPathwayId, activityConfig) => {
    const payload = { activityId, parentPathwayId, activityConfig };
    const newActivity = await activityResolver.duplicateActivityInPathway(payload);
    const target = await CoursewareTargetService.build().createTarget(newActivity.activityId);
    await annotationService.refetchAnnotations(target);
    _parseAndSaveToCache(newActivity);
    pathwayService.notifyUpdate(parentPathwayId);
    return newActivity.activityId;
  };

  const updateDuplicatedActivityTitle = async (newActivityId) => {
    const activityDetails = await getActivity(newActivityId);
    activityDetails.config.title = `${intl.formatMessage(messages.copyOf)}${activityDetails.config.title}`;
    saveActivity(newActivityId, activityDetails.config);
  };

  const getParentActivityId = async (activityId) => {

    let parentActivityId = null;
    const { ancestry } = await CoursewareTargetService.build().createTarget(activityId);

    for (const coursewareElement of ancestry) {
      if (coursewareElement.elementType === elementTypes.ACTIVITY) {
        parentActivityId = coursewareElement.elementId;
        break;
      }
    }
    return parentActivityId;
  };

  const getRootActivityId = async (activityId) => {
    if (!activityId) {
      return null;
    }
    const { ancestry = [] } = await CoursewareTargetService.build().createTarget(activityId);
    const rootActivityId = ancestry.length ? ancestry[ancestry.length - 1].elementId : activityId;
    return rootActivityId;
  };

  const getSchemasInActivity = async (activityId, category) => {
    const configurationData = await getInActivityConfig(activityId);
    const { pluginId, version, name: pluginName } = await getInActivity(activityId, keyPaths.pluginRef);
    const studentScopeURN = await getInActivity(activityId, ['studentScopeURN']);
    const plugin = await PluginService.getPlugin(pluginId, version);
    const configurationSchema = JSON.parse(plugin.manifest.configurationSchema || '{}');

    return [
      {
        key: activityId,
        scope: {
          sourceId: activityId,
          studentScopeURN,
        },
        label: (configurationData && configurationData.title) || pluginName || activityId,
        category,
        schema: { ...configurationSchema },
      },
    ];
  };

  const authorActivityAlfrescoSync = async (activityId) => {
    const response = await activityResolver.authorActivityAlfrescoSync(activityId);
    return response;
  };

  const getBreadcrumb = async (elementId, elementType = 'ACTIVITY') => {
    const { projectId, workspaceId, breadcrumb } = await activityResolver.authorActivityBreadcrumb({ elementId, elementType });
    // save ids in the cache
    if (elementType === 'ACTIVITY') {
      setAncestorIdsInActivity(elementId, projectId, workspaceId);
    }
    return [...breadcrumb];
  };

  const getCoursewareStructure = async (elementId, fieldNames, elementType) => {
    const { coursewareStructure } = await activityResolver.authorCoursewareStructureNavigate({ elementId, elementType, fieldNames });
    return coursewareStructure;
  };

  const getStructure = async (elementId, ancestry, fieldNames, elementType) => {
    const { coursewareStructure } = await activityResolver.authorCoursewareStructureNavigate({ elementId, elementType, fieldNames });

    let _ancestry = ancestry.slice().reverse();
    _ancestry = _ancestry.slice(0, -2);

    const {
      elementId: parentElementId,
      elementType: parentElementType,
    } = _ancestry[_ancestry.length - 1];

    const parentActivity = await activityResolver.authorCoursewareStructureNavigate({
      elementId: parentElementId,
      elementType: parentElementType,
      fieldNames,
    });

    let ancestryIds = _ancestry.map((element) => element.elementId);
    const childActivityIds = parentActivity?.coursewareStructure?.children?.[0].children?.map((item) => item.elementId);

    if (childActivityIds && childActivityIds.length) {
      ancestryIds = ancestryIds.concat(childActivityIds);
    }

    const childCoursewareStructure = await getStructureChildren(coursewareStructure, ancestryIds, fieldNames);
    return { coursewareStructure: childCoursewareStructure };
  };

  const getStructureChildren = async (structure = {}, ancestryIds, fieldNames) => {
    const _structure = { ...structure };
    const inAncestry = ancestryIds.some((elementId) => elementId === _structure.elementId);

    if (_structure.hasChildren && !_structure.children && inAncestry) {
      const { coursewareStructure } = await activityResolver.authorCoursewareStructureNavigate({
        elementId: _structure.elementId,
        elementType: _structure.type,
        fieldNames,
      });
      _structure.children = coursewareStructure.children;
    }

    if (_structure.children) {
      Promise.all(_structure.children.map(async (childItem) => {
        if (childItem.children || childItem.hasChildren) {
          return await getStructureChildren(childItem, ancestryIds, fieldNames);
        }
        return childItem;
      })).then((children) => {
        _structure.children = children;
      });
    }

    return _structure;
  };

  /**
   *  An object containing workspaceId and projectId
   *  @typedef {Object} AncestorIds
   *  @property {string} workspaceId id of the workspace to which activity belongs
   *  @property {string} projectId id of the project to which activity belongs
   */

  /**
    * To get current workspace id and project Id
    * @param {String} activityId
    * @returns {AncestorIds} AncestorIds
    */
  const getAncestorIds = async (activityId) => {
    if (_activityCache.hasIn([activityId, keyPaths.ANCESTORS_KEY])) {
      return _activityCache.getIn([activityId, keyPaths.ANCESTORS_KEY]).toJS();
    }

    await getBreadcrumb(activityId); // ensures activityCache has the ancestors
    return _activityCache.getIn([activityId, keyPaths.ANCESTORS_KEY]).toJS();
  };

  const createSubActivity = async (parentPathwayId, interactiveId) => {
    const subActivityName = 'Embeddable Lesson';
    const subActivityType = 'lesson';
    const subActivityConfig = { title: 'Sub Activity' };
    const plugin = await PluginService.getOfficialPluginByName(subActivityName, subActivityType);
    if (!plugin) {
      throw new Error(`Cannot find official ${subActivityName} plugin`);
    }

    const { manifest, summary: { pluginId, latestVersion, name } } = plugin;
    const schema = JSON.parse(manifest.configurationSchema);
    const pathwayProperties = findPropertiesByType(schema, pluginTypes.PATHWAY);
    const pathwayProperty = pathwayProperties && pathwayProperties[0];

    if (!pathwayProperty) {
      throw new Error(`Cannot find suitable pathway property within the plugin ${name}`);
    }

    const newSubActivityId = await createActivity(
      pluginId,
      latestVersion,
      schema,
      subActivityConfig,
      false,
      parentPathwayId
    );

    const newPathwayId = await pathwayService.createPathway(
      newSubActivityId,
      pathwayProperty.path,
      pathwayTypes.PATHWAY_TYPE_LINEAR,
      schema[pathwayProperty.path[0]]?.preload
    );

    // Optionally move an existing interactive into the new group
    if (!interactiveId) { return; }
    await interactiveService.moveInteractiveToPathway(interactiveId, newPathwayId);
    await interactiveService.deleteInteractive(interactiveId, parentPathwayId, false);

    // Activities don't current handle live changes outside of the config object, so instead of messing
    // with the cache, just refetch the whole thing (at this point it should be very light anyway).
    _forgetActivity(newSubActivityId);
    await getActivity(newSubActivityId);
    await pathwayService.notifyUpdate(newPathwayId);
  };

  const getChildrenFromPathways = (pathways) => {
    const childrenFromPathways = pathways.reduce((acc, pathway) => {
      if (pathway?.children) {
        const activityTypeChild = pathway.children.filter(({ elementType }) => elementType === elementTypes.ACTIVITY);
        return [...acc, ...activityTypeChild];
      }
      return acc;
    }, []);

    return childrenFromPathways;
  };

  /**
   * Returns a list of children of a specicifed type for a given activity to a depth 
   * the user specifies.
   * 
   * Defaults to full depth unless otherwise specified 
   * 
   * @param {string} activityId root activityId
   * @param {string} childType what kind of activities do you want? eg. 'lesson'
   * @param {number | '*'} depthRequired number | '*' - how many levels deep do you want to go, defaults to * which means every level
   * @param {number} depthAchieved refers to how many levels found activities of type required eg. if children of starting activity were of the correct type, it will
   * increase the depthAchieved, if not it will go deeper, if it finds children of desired type there it will increment, if not it will keep looking - used for recursion
   * @param {Array<Activity>} activitiesOfType an array of Activities found with the specified type
   * @param {boolean} bypassCache Defaults to false, can be set to true if you don't want to use the cached activity  - used for recursion
   * 
   * @return {Array<Activity>} the activities of the type desired.
   */
  const getChildActivitiesByType = async (activityId, childType, depthRequired = '*', depthAchieved = 0, activitiesOfType = [], bypassCache = false) => {

    if (activityId == null || childType == null) {
      // eslint-disable-next-line no-console
      console.error(`Error: ActivityService.getChildrenByType, called with missing parameters activityId: ${activityId}, childType:${childType}.`);
      return;
    }

    if (depthAchieved === depthRequired) {
      return activitiesOfType;
    }

    try {
      const children = await getChildActivities(activityId, bypassCache);
      const filteredActivities = children.filter((child) => child.plugin.type.toLowerCase() === childType.toLowerCase());
      const pathwayIds = children.map((child) => child.childrenPathways || []).flat();

      if (filteredActivities.length > 0) {
        activitiesOfType.push(...filteredActivities);
        depthAchieved++;
      }

      const continueSearchingForActivities = pathwayIds.length > 0 && (depthRequired === '*' || depthAchieved < depthRequired);

      if (continueSearchingForActivities) {
        //get the pathways and basic child info for each one
        const pathways = await Promise.all(pathwayIds.map((pathwayId) => pathwayService.getPathway(pathwayId)));
        const pathwayChildren = getChildrenFromPathways(pathways);

        //get the full activity from that child info and filter by desired type
        const activities = await Promise.all(pathwayChildren.map((child) => getActivity(child.elementId)));
        const filteredActivities = activities.filter((child) => child.plugin.type.toLowerCase() === childType.toLowerCase());

        if (filteredActivities.length > 0) {
          activitiesOfType.push(...filteredActivities);
          depthAchieved++;
        }

        //repeat the check for each of these activities       
        await Promise.all(pathwayChildren.map((child) => getChildActivitiesByType(child.elementId, childType, depthRequired, depthAchieved, activitiesOfType)));
      }

      return activitiesOfType;

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ActivityService.getChildrenByType, error: ", error);
    }
  };

  const getChildActivities = async (activityId, bypassCache = false) => {
    const activity = await getActivity(activityId, bypassCache);
    const { childrenPathways = [] } = activity;
    const allPathways = await Promise.all(childrenPathways.map((pathway) => pathwayService.getPathway(pathway)));
    const children = getChildrenFromPathways(allPathways);
    return Promise.all(children.map(({ elementId }) => getActivity(elementId)));
  };

  const ungroupActivity = async (parentPathwayId, activityId) => {
    const childActivity = await getActivity(activityId);
    if (!childActivity.childrenPathways || childActivity.childrenPathways.length === 0) {
      return;
    }

    const childrenWalkables = await Promise.all(childActivity.childrenPathways.map(async (pathwayId) => {
      const { children } = await pathwayService.getPathway(pathwayId);
      return { pathwayId, children };
    }));

    const parentPathway = await pathwayService.getPathway(parentPathwayId);
    const parentPathwayWalkablesOrder = parentPathway.children.map(({ elementId }) => elementId);
    const indexOfActivity = parentPathwayWalkablesOrder.indexOf(activityId);

    // flatten children to make it easier to work with
    const flattenedChildrenElements = childrenWalkables.flatMap(({ children }) => children);
    const flattenedChildrenIds = flattenedChildrenElements.map(({ elementId }) => elementId);
    const childIdsTrackingMap = {};

    if (!flattenedChildrenIds || flattenedChildrenIds.length === 0) {
      return;
    }

    // Duplicate all children into the parent pathway.
    await Promise.all(flattenedChildrenElements.map(async ({ elementId, elementType }) => {
      let walkableId;
      if (elementType === elementTypes.INTERACTIVE) {
        await interactiveService.moveInteractiveToPathway(elementId, parentPathwayId);
      } else if (elementType === elementTypes.ACTIVITY) {
        walkableId = await duplicateActivity(activityId, parentPathwayId);
      }

      if (walkableId) {
        childIdsTrackingMap[elementId] = walkableId;
      }

    }));

    // Delete the original activity
    await deleteActivity(activityId, parentPathwayId);
    if (childIdsTrackingMap.length) {
      // We now need to clean up the order of walkables (since the above operations are all syncronous)
      parentPathwayWalkablesOrder.splice(
        indexOfActivity,
        1,
        ...flattenedChildrenIds.map((originalChildId) => childIdsTrackingMap[originalChildId]),
      );

      // Apply the new order up the ordering
      await pathwayService.reorderPathwayWalkables(parentPathwayId, parentPathwayWalkablesOrder);
    }
  };

  /**
   * Registers an activity to a student scope.
   * @param {string} activityId
   */
  const authorStudentScopeRegisterActivity = async (activityId) => {
    if (!has(activityId)) {
      throw new Error('Activity not available');
    }

    const activity = _activityCache.get(activityId).toJS();
    await activityResolver.authorStudentScopeRegister({
      studentScopeURN: activity.studentScopeURN,
      elementId: activityId,
      elementType: elementTypes.ACTIVITY,
    });
  };

  /**
   * Deregister an activity from a student scope
   * @param {string} activityId
   */
  const authorStudentScopeDeregisterActivity = async (activityId) => {
    if (!has(activityId)) {
      throw new Error('Activity not available');
    }

    const activity = _activityCache.get(activityId).toJS();

    await activityResolver.authorStudentScopeDeregister({
      studentScopeURN: activity.studentScopeURN,
      elementId: activityId,
      elementType: elementTypes.ACTIVITY,
    });
  };

  /**
   * Registers an Component to a student scope.
   * Registers a component, rather than the activity.
   * @param {string} activityId
   * @param {string} componentId
   */
  const authorStudentScopeRegisterComponentOrInteractive = async (activityId, sourceId, elementType, studentScopeURN, bypassCache = false) => {
    if (!has(activityId) && !bypassCache) {
      throw new Error('Activity not available');
    }
    let activity;
    if (!bypassCache) {
      activity = _activityCache.get(activityId).toJS();
    }
    await activityResolver.authorStudentScopeRegister({
      studentScopeURN: studentScopeURN || activity.studentScopeURN,
      elementId: sourceId,
      elementType: elementType || elementTypes.COMPONENT,
    });
  };

  /**
   * Deregister a component from a student scope
   * Deregister a component, rather than the activity.
   * @param {string} activityId
   * @param {string} componentId
   */
  const authorStudentScopeDeregisterComponent = async (activityId, componentId) => {
    if (!has(activityId)) {
      throw new Error('Activity not available');
    }

    const activity = _activityCache.get(activityId).toJS();

    await activityResolver.authorStudentScopeDeregister({
      studentScopeURN: activity.studentScopeURN,
      elementId: componentId,
      elementType: elementTypes.COMPONENT,
    });
  };

  /**
   * Associate an activity with a theme
   * @param {string} activityId
   * @param {string} themeId
   */
  const authorElementThemeCreate = async ({ activityId, themeId }) => {
    const result = await activityResolver.authorElementThemeCreate({
      elementId: activityId,
      elementType: 'ACTIVITY',
      themeId,
    });
    return result;
  };

  /**
   * Remove the associated an activity with a theme
   * @par am {string} activityId
   */
  const authorElementThemeDelete = async ({ activityId }) => {
    const result = await activityResolver.authorElementThemeDelete({
      elementId: activityId,
      elementType: 'ACTIVITY',
    });
    return result;
  };

  const getSubActivitiesScreens = async (activityId) => {
    let screenIds = [];
    const getSubActivities = await getChildActivities(activityId);
    if (getSubActivities && getSubActivities.length > 0) {
      await Promise.all(getSubActivities?.map(async (subActivity) => {
        const pathWayElementIds = await pathwayService.getPathway(subActivity?.childrenPathways[0]);
        pathWayElementIds.children?.forEach((element) => {
          screenIds.push(element?.elementId);
        });
      }));
    }
    return screenIds;
  };

  const coursewareConditionalGet = async ( courseId ) => {
    const result = await activityResolver.coursewareConditionalGet(courseId);
    return result;
  };

  const coursewareConditionalAdd = async ( courseId, name ) => {
    const result = await activityResolver.coursewareConditionalAdd(courseId, name);
    return result;
  };

  const coursewareConditionalRemove = async ( courseId, name ) => {
    const result = await activityResolver.coursewareConditionalRemove(courseId, name);
    return result;
  };

  const coursewareLanguagesGet = async ( courseId ) => {
    const result = await activityResolver.coursewareLanguagesGet(courseId);
    return result;
  };

  const coursewareLanguagesAdd = async ( courseId, languageList ) => {
    const result = await activityResolver.coursewareLanguagesAdd(courseId, languageList);
    return result;
  };

  const coursewareLanguageTranslate = async ({ action, correlationId, exportId,  elementId , elementType, language, userId}  ) => {
    const coursewareStructure = await getCoursewareStructure(elementId, null, elementType);
    const structure = JSON.stringify(coursewareStructure);
    const result = await activityResolver.coursewareLanguageTranslate({action, correlationId, exportId, elementId, elementType, language, userId, structure});
    return result;
  };

  return {
    keyPaths,
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      activityResolver = new ActivityResolver(connectionProvider);
    },

    has,
    createActivity,
    createWorkspaceActivity,
    createProjectActivity,
    createInActivity,
    addChildInActivity,

    saveActivity,
    saveInActivityConfig,
    saveInActivityIngestionUpdate,
    setInPreviewScope,
    saveInActivityPreviewScope,
    setComponentInActivity,
    setAncestorIdsInActivity,
    saveActivityTheme,
    saveActivityThemePayload,

    getActivity,
    getActivityPlugIn,
    getInActivity,
    getInActivityConfig,
    getInActivityConfigHelper,
    getInActivityTheme,
    getInSelectedTheme,
    getParentActivityId,
    getRootActivityId,
    getWorkspaceActivityList,
    publishWorkspaceActivity,
    subscribeToActivity,
    subscribeToActivityConfigPart,
    subscribeToThemePart,

    deleteActivity,
    duplicateActivity,
    updateDuplicatedActivityTitle,

    createSubActivity,
    ungroupActivity,
    moveActivity,
    authorActivityAlfrescoSync,

    getSchemasInActivity,
    notifyUpdate,

    getBreadcrumb,
    getStructure,
    getStructureChildren,
    getChildActivities,
    getChildActivitiesByType,
    getAncestorIds,
    getCoursewareStructure,
    authorStudentScopeRegisterActivity,
    authorStudentScopeDeregisterActivity,
    authorStudentScopeRegisterComponentOrInteractive,
    authorStudentScopeDeregisterComponent,

    authorElementThemeCreate,
    authorElementThemeDelete,
    authorActivityIconLibraryAssociate,
    getSubActivitiesScreens,

    coursewareConditionalGet,
    coursewareConditionalAdd,
    coursewareConditionalRemove,

    coursewareLanguagesGet,
    coursewareLanguagesAdd,

    coursewareLanguageTranslate,
  };
})();
